// src/pages/ProgramsPage.js
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const ProgramsPage = ({ language }) => {
  const [activeProgram, setActiveProgram] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_BASE_URL + "training/get-all-trainings"
        );

        if (response.data.status === "success") {
          const apiPrograms = response.data.data.map((program) => ({
            id: program.id,
            title: {
              en: program.title,
              ar: program.title_ar,
            },
            description: {
              en: program.description,
              ar: program.description_ar,
            },
            manual: {
              en: "Program Manual",
              ar: "دليل البرنامج",
              file: program.manual,
              fileType: program.manualType,
            },
            materials: program.materials.map((material) => ({
              id: material.id,
              type: material.type,
              title: {
                en: material.title,
                ar: material.title_ar,
              },
              file: material.file,
              fileType: material.fileType,
            })),
            alumni: {
              en: "Program Alumni",
              ar: "خريجي البرنامج",
            },
            media: {
              en: "Program Media",
              ar: "وسائط البرنامج",
            },
          }));

          setPrograms(apiPrograms);
        }
      } catch (error) {
        console.error("Error fetching programs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "Behavior":
        return "🧠";
      case "Skill":
        return "⚡";
      case "Knowledge":
        return "📖";
      default:
        return "📄";
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      Behavior: { en: "Behavior", ar: "سلوكي" },
      Skill: { en: "Skill", ar: "مهارة" },
      Knowledge: { en: "Knowledge", ar: "معرفة" },
    };
    return labels[type]?.[language] || type;
  };

  const handleItemClick = (type) => {
    const currentProgram = programs[activeProgram];

    switch (type) {
      case "manual":
        if (currentProgram.manual.file) {
          window.open(
            process.env.REACT_APP_BASE_URL + currentProgram.manual.file,
            "_blank"
          );
        }
        break;
      case "materials":
        setShowMaterialsPopup(true);
        break;
      case "alumni":
        console.log("Alumni section clicked");
        break;
      case "media":
        console.log("Media section clicked");
        break;
      default:
        break;
    }
  };

  const handleMaterialClick = (material) => {
    if (material.file) {
      window.open(process.env.REACT_APP_BASE_URL + material.file, "_blank");
    }
  };

  // Group materials by type
  const groupedMaterials =
    programs[activeProgram]?.materials?.reduce((groups, material) => {
      const type = material.type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(material);
      return groups;
    }, {}) || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-green-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-green-200 rounded w-48 mx-auto mb-4"></div>
              <div className="h-1 bg-green-200 rounded w-24 mx-auto mb-4"></div>
              <div className="h-4 bg-green-200 rounded w-64 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="min-h-screen bg-green-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-green-900 mb-4">
            {language === "ar"
              ? "جميع البرامج التدريبية"
              : "All Training Programs"}
          </h2>
          <p className="text-gray-600">
            {language === "ar"
              ? "لا توجد برامج متاحة حالياً"
              : "No programs available at the moment"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-green-50 py-12">
        <div
          className="container mx-auto px-4"
          style={{
            marginTop: "10rem",
          }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <button
              onClick={handleBack}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg mb-6"
            >
              {language === "ar" ? "العودة" : "Back"}
            </button>

            <h1 className="text-4xl md:text-5xl font-bold text-green-900 mb-4">
              {language === "ar"
                ? "جميع البرامج التدريبية"
                : "All Training Programs"}
            </h1>
            <div className="w-32 h-1 bg-green-600 mx-auto"></div>
            <p className="text-gray-600 max-w-2xl mx-auto mt-4">
              {language === "ar"
                ? "استكشف جميع برامجنا التدريبية الشاملة المصممة لتطوير مهارات وقدرات العاملين"
                : "Explore all our comprehensive training programs designed to develop employee skills and capabilities"}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
            {programs.map((program, index) => (
              <button
                key={program.id}
                onClick={() => setActiveProgram(index)}
                className={`p-6 rounded-xl text-left transition-all ${
                  activeProgram === index
                    ? "bg-green-800 text-white shadow-lg transform -translate-y-1"
                    : "bg-white text-gray-700 hover:bg-green-100 shadow-md"
                }`}
              >
                <h3 className="font-bold text-xl mb-3">
                  {program.title[language]}
                </h3>
                <p className="text-sm opacity-80 leading-relaxed">
                  {program.description[language]}
                </p>
              </button>
            ))}
          </div>

          {/* Program Details */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-8">
              <h3 className="text-3xl font-bold text-green-900 mb-4">
                {programs[activeProgram]?.title[language]}
              </h3>
              <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                {programs[activeProgram]?.description[language]}
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    key: "manual",
                    icon: "📋",
                    label: programs[activeProgram]?.manual[language],
                  },
                  {
                    key: "materials",
                    icon: "📚",
                    label:
                      language === "ar"
                        ? "المواد التدريبية"
                        : "Training Materials",
                  },
                  {
                    key: "alumni",
                    icon: "👥",
                    label: programs[activeProgram]?.alumni[language],
                  },
                  {
                    key: "media",
                    icon: "🎬",
                    label: programs[activeProgram]?.media[language],
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleItemClick(item.key)}
                    className={`border-2 border-green-200 rounded-xl p-6 text-center hover:bg-green-50 transition-all duration-300 group cursor-pointer ${
                      item.key === "manual" &&
                      !programs[activeProgram]?.manual.file
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:border-green-300"
                    }`}
                  >
                    <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                      {item.icon}
                    </div>
                    <h4 className="font-semibold text-green-800 text-lg">
                      {item.label}
                    </h4>
                    {item.key === "materials" &&
                      programs[activeProgram]?.materials && (
                        <p className="text-sm text-gray-500 mt-2">
                          {programs[activeProgram].materials.length}{" "}
                          {language === "ar" ? "عنصر" : "items"}
                        </p>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Materials Popup */}
      {showMaterialsPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-green-800 text-white p-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold">
                  {language === "ar"
                    ? "المواد التدريبية"
                    : "Training Materials"}
                </h3>
                <button
                  onClick={() => setShowMaterialsPopup(false)}
                  className="text-white hover:text-green-200 text-2xl font-bold transition-colors"
                >
                  ×
                </button>
              </div>
              <p className="text-green-100 mt-2">
                {programs[activeProgram]?.title[language]}
              </p>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {Object.keys(groupedMaterials).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {language === "ar"
                    ? "لا توجد مواد متاحة"
                    : "No materials available"}
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedMaterials).map(([type, materials]) => (
                    <div
                      key={type}
                      className="border border-green-200 rounded-xl overflow-hidden"
                    >
                      {/* Type Header */}
                      <div className="bg-green-50 p-4 border-b border-green-200">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse">
                          <span className="text-2xl">{getTypeIcon(type)}</span>
                          <h4 className="font-bold text-green-900 text-lg">
                            {getTypeLabel(type)}
                          </h4>
                          <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-sm">
                            {materials.length}{" "}
                            {language === "ar" ? "عنصر" : "items"}
                          </span>
                        </div>
                      </div>

                      {/* Materials List */}
                      <div className="divide-y divide-green-100">
                        {materials.map((material) => (
                          <div
                            key={material.id}
                            onClick={() => handleMaterialClick(material)}
                            className="p-4 hover:bg-green-50 transition-colors cursor-pointer group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                                <span className="text-2xl text-green-600 group-hover:scale-110 transition-transform">
                                  {material.fileType === "pdf" ? "📄" : "📎"}
                                </span>
                                <div>
                                  <h5 className="font-semibold text-green-900 group-hover:text-green-700">
                                    {material.title[language]}
                                  </h5>
                                  <p className="text-sm text-gray-500 capitalize">
                                    {material.fileType} •{" "}
                                    {material.file?.split("/").pop()}
                                  </p>
                                </div>
                              </div>
                              <span className="text-green-600 group-hover:text-green-800 transform group-hover:translate-x-1 transition-transform">
                                {language === "ar" ? "عرض" : "View"} →
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-green-200 p-4 bg-green-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowMaterialsPopup(false)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors font-semibold"
                >
                  {language === "ar" ? "إغلاق" : "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProgramsPage;
